import { ChatAgentMessage } from "@components/ChatAgent";
import { Slide, useTheme } from "@mui/material";
import React, { useMemo } from "react";
import { UserDadaType } from "@context/auth/types";
import AnimatedChatLoadingComponent from "@components/ChatAgent/components/AnimatedChatLoading/AnimatedChatLoading.component";
import {
  AvatarContainer,
  CheckMark,
  Container,
  MessageBox,
  MessageContainer,
  MessageMeta,
  RateExperienceLink,
  RateExperienceRow,
  SuggestedQuestionChip,
  SuggestedQuestionsRow,
  Timestamp,
} from "@components/ChatAgent/components/ChatAgentMessageBox/style";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslation } from "react-i18next";
import MermaidBlock from "@components/ChatAgent/components/MermaidBlock/MermaidBlock";

import { Bot, Check, Sparkles, User } from "lucide-react";

// Frasi di apertura che l'agente a volte mette prima dell'elenco
// dei suggerimenti, da rimuovere insieme alle domande.
const SUGGESTED_HEADER_PATTERNS = [
  "domande suggerite",
  "risposte suggerite",
  "potresti chiedere",
  "potresti chiedermi",
  "potresti voler sapere",
  "ecco alcune domande",
  "suggested questions",
  "follow[- ]up questions",
  "you might (?:also )?ask",
];

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Wrapper opzionale di grassetto/corsivo attorno alla domanda (l'agente spesso scrive i bullet dei suggerimenti in **grassetto**).
const EMPH = "(?:\\*\\*|__|\\*|_)?";

// Toglie dal testo del messaggio le domande che vengono già mostrate come pillole sotto, per evitare il duplicato.
const stripSuggestedFromBody = (
  body: string,
  suggestions: string[] | undefined,
): string => {
  if (!body) return body;
  let out = body;
  if (suggestions && suggestions.length) {
    for (const q of suggestions) {
      const trimmed = q?.trim();
      if (!trimmed) continue;
      const esc = escapeRegex(trimmed.replace(/[?!.:;]+$/g, ""));
      const lineRe = new RegExp(
        `^[ \\t]*(?:[-*+]|\\d+[.)])?[ \\t]*${EMPH}[ \\t]*${esc}[ \\t]*[?!.:;]?[ \\t]*${EMPH}[ \\t]*$\\r?\\n?`,
        "gim",
      );
      out = out.replace(lineRe, "");
    }
  }
  const headerRe = new RegExp(
    `^[ \\t]*[*_]*\\s*(?:${SUGGESTED_HEADER_PATTERNS.join(
      "|",
    )})\\s*[:：]?\\s*[*_]*[ \\t]*$\\r?\\n?`,
    "gim",
  );
  out = out.replace(headerRe, "");
  return out.replace(/\s+$/g, "");
};

const markdownComponents = {
  code: ({
    className,
    children,
    ...props
  }: React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }) => {
    const match = /language-(\w+)/.exec(className || "");
    const lang = match?.[1];
    if (lang === "mermaid") {
      return <MermaidBlock code={String(children ?? "").replace(/\n$/, "")} />;
    }
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
};

interface ChatAgentMessageBoxProps {
  index: number;
  message: ChatAgentMessage;
  user?: UserDadaType;
  onSuggestionClick?: (text: string) => void;
  onRateExperience?: (messageIndex: number) => void;
  showRateExperience?: boolean;
}

const ChatAgentMessageBox = ({
  index,
  message,
  user,
  onSuggestionClick,
  onRateExperience,
  showRateExperience,
}: ChatAgentMessageBoxProps) => {
  const theme = useTheme();
  const { t } = useTranslation("agent_chat");
  const isAI = message.sender === "assistant";
  const cleanedResponse = useMemo(
    () =>
      isAI
        ? stripSuggestedFromBody(message.response, message.suggested_questions)
        : message.response,
    [isAI, message.response, message.suggested_questions],
  );
  const hasContent = cleanedResponse !== "";

  return (
    <Slide direction={"up"} in mountOnEnter unmountOnExit>
      <div>
        <Container isAI={isAI} messageIndex={index} gap={"10px"}>
          <AvatarContainer isAI={isAI}>
            {isAI ? (
              <Bot size={20} color="#000" />
            ) : (
              <User size={20} color="#fff" />
            )}
          </AvatarContainer>
          <MessageContainer theme={theme} isAI={isAI}>
            {!hasContent ? (
              <AnimatedChatLoadingComponent />
            ) : (
              <MessageBox isAI={isAI}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {cleanedResponse}
                </ReactMarkdown>
              </MessageBox>
            )}
          </MessageContainer>
        </Container>

        {hasContent && (
          <MessageMeta isAI={isAI}>
            {message.timestamp && <Timestamp>{message.timestamp}</Timestamp>}
            {!isAI && (
              <CheckMark>
                <Check size={12} strokeWidth={3} />
                <Check
                  size={12}
                  strokeWidth={3}
                  style={{ marginLeft: "-7px" }}
                />
              </CheckMark>
            )}
            {isAI && hasContent && showRateExperience && onRateExperience && (
              <RateExperienceRow>
                <Timestamp>{t("rateExperiencePrompt")}</Timestamp>
                <RateExperienceLink
                  variant="text"
                  onClick={() => onRateExperience(index)}
                >
                  {t("rateExperience")}
                </RateExperienceLink>
              </RateExperienceRow>
            )}
          </MessageMeta>
        )}

        {isAI &&
          hasContent &&
          message.suggested_questions &&
          message.suggested_questions.length > 0 && (
            <SuggestedQuestionsRow>
              {message.suggested_questions.map((q, qi) => (
                <SuggestedQuestionChip
                  key={`sugg_${index}_${qi}`}
                  onClick={() => onSuggestionClick?.(q)}
                  disabled={!onSuggestionClick}
                  startIcon={<Sparkles size={14} />}
                >
                  {q}
                </SuggestedQuestionChip>
              ))}
            </SuggestedQuestionsRow>
          )}
      </div>
    </Slide>
  );
};

export default ChatAgentMessageBox;
